import { Flex, Form, Radio, Select } from 'antd';
import { ConfigProvider } from 'antd/lib';
import dayjs from 'dayjs';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { usePermissions } from 'business/contextProviders/usePermissions';
import {
  useIndicatorAnalyzeFilters,
  useIndicatorAnalyzeFiltersFormName,
} from 'business/shift-report/indicators/pages/indicator-analyze/providers/filters';
import {
  INDICATOR_DISPLAY_TYPES,
  INDICATOR_GROUP_BY_TYPES,
  IndicatorAnalyzeRangeFilters,
} from 'business/shift-report/indicators/pages/indicator-analyze/providers/filters/types';
import {
  Module_Enum,
  useGetCurrentRingShiftReportQuery,
} from 'generated/graphql';
import NumberRangeInput from 'ui/form/number-range-input';
import TemporalitySelectInput, {
  DateSelectionTypeEnum,
} from 'ui/form/temporality-select-input';

type IndicatorAnalyzeFiltersProps = {
  noDefaultFor?: DateSelectionTypeEnum[];
};

export const IndicatorAnalyzeFilters = ({
  noDefaultFor,
}: IndicatorAnalyzeFiltersProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<IndicatorAnalyzeRangeFilters>();
  const formName = useIndicatorAnalyzeFiltersFormName();
  const filters = useIndicatorAnalyzeFilters();

  const { constructionSiteId } = usePermissions();
  const gqlHeaders = useGraphqlApiHeaders(Module_Enum.ShiftReport);
  const { data, error } = useGetCurrentRingShiftReportQuery({
    variables: { constructionSiteId },
    context: {
      headers: gqlHeaders,
    },
  });

  return (
    <ConfigProvider
      theme={{ components: { Form: { inlineItemMarginBottom: 12 } } }}
    >
      <Form form={form} name={formName} initialValues={filters} layout="inline">
        <Form.Item
          name="filterType"
          label={t('input.filterByOptions.label')}
          layout="vertical"
        >
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            options={[
              {
                label: t('input.filterByOptions.date'),
                value: 'date',
              },
              {
                label: t('input.filterByOptions.ring'),
                value: 'ring',
              },
            ]}
          />
        </Form.Item>

        <Form.Item
          shouldUpdate={(prevValues, curValues) =>
            prevValues.filterType !== curValues.filterType
          }
          noStyle
        >
          {({ getFieldValue }) => {
            const filterType = getFieldValue('filterType');

            if (filterType === 'date') {
              return (
                <Flex>
                  <Form.Item
                    name="dateRange"
                    label={t('input.dateRange.label')}
                    layout="vertical"
                  >
                    <TemporalitySelectInput
                      defaultDate={dayjs()}
                      noDefaultFor={noDefaultFor}
                      defaultType={DateSelectionTypeEnum.Period}
                    />
                  </Form.Item>
                </Flex>
              );
            } else if (filterType === 'ring') {
              return (
                <Form.Item
                  name="ringRange"
                  label={t('input.ringRange.label')}
                  layout="vertical"
                >
                  <NumberRangeInput
                    leftInputProps={{
                      min: 0,
                      placeholder: t('input.ringRange.startPlaceholder'),
                    }}
                    rightInputProps={{
                      min: 0,
                      placeholder: t('input.ringRange.endPlaceholder'),
                    }}
                    bottomMessage={
                      error
                        ? t('dataAnalysis.errors.currentRingNumberError')
                        : t('dataAnalysis.labels.currentRingNumber', {
                            ringNumber: data?.task.at(0)?.ring,
                          })
                    }
                    allowClear
                  />
                </Form.Item>
              );
            }
            return null;
          }}
        </Form.Item>

        {filters.filterType === 'date' ? (
          <Form.Item
            name="groupByType"
            label={t('reportAnalyze.indicatorAnalyze.groupByOptions')}
            style={{ minWidth: '110px' }}
            layout="vertical"
          >
            <Select
              popupMatchSelectWidth={false}
              options={INDICATOR_GROUP_BY_TYPES.map((value) => ({
                label: t(`reportAnalyze.indicatorAnalyze.groupByOptions`, {
                  context: value,
                }),
                value,
              }))}
            />
          </Form.Item>
        ) : null}

        <Form.Item
          name="displayType"
          label={t('reportAnalyze.indicatorAnalyze.displayTypeOptions')}
          layout="vertical"
        >
          <Select
            popupMatchSelectWidth={false}
            options={INDICATOR_DISPLAY_TYPES.map((value) => ({
              label: t(`reportAnalyze.indicatorAnalyze.displayTypeOptions`, {
                context: value,
              }),
              value,
            }))}
          />
        </Form.Item>
      </Form>
    </ConfigProvider>
  );
};
